import React, { useEffect, useRef } from 'react';
import ringGeometry from './ringGeometry';

/*
 * O anel do logo como um objeto de luz, desenhado num fragment shader
 * (WebGL1 puro, sem three.js). É um círculo exato, com a abertura do logo
 * à direita centrada na horizontal, fluxo de energia correndo pelo traço,
 * reação ao ponteiro e abertura conforme o hero sai da tela.
 *
 * `state` é um objeto mutável controlado de fora (GSAP escreve nele):
 *   intro  0..1  presença geral
 *   draw   0..1  quanto do anel já foi traçado
 *   scroll 0..1  progresso de saída do hero
 */

const VERT = `
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;
uniform vec2 uRes;
uniform float uTime;
uniform vec2 uCenter;
uniform float uRadius;
uniform vec2 uMouse;
uniform float uMouseOn;
uniform float uIntro;
uniform float uDraw;
uniform float uScroll;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise(p); p *= 2.03; a *= 0.5; }
  return v;
}

const float PI = 3.14159265;

void main() {
  vec2 frag = gl_FragCoord.xy;
  float R = uRadius * (1.0 + uScroll * 1.35);
  vec2 p = (frag - uCenter) / R;
  float r = length(p);
  float ang = atan(p.y, p.x);
  float deg = degrees(ang);
  float t = uTime;

  // Ruído amostrado sobre o círculo (cos, sin), não sobre o ângulo cru:
  // atan salta de +180° para -180° no lado esquerdo e criaria uma emenda.
  vec2 dirA = vec2(cos(ang), sin(ang));

  // O traço é um círculo exato. A vida vem da luz (fluxo e brilho), não da forma:
  // variar o raio com ruído deixava o anel visivelmente fora de esquadro neste tamanho.
  float wob = fbm(dirA * 1.3 + vec2(t * 0.12, t * 0.08));
  float rr = 1.0;
  float d = abs(r - rr);

  // Abertura do logo (89° de arco), centrada na horizontal: as duas pontas ficam
  // na mesma vertical. No logo ela é inclinada 4,5° para cima, o que some ao lado
  // do nome mas aparece neste tamanho. Abre mais ao rolar, por igual dos dois lados.
  // O traço visível é um ARCO que começa na ponta de cima (hi) e percorre
  // uDraw da volta. A distância é medida até o arco de verdade: sobre o
  // arco é |r - rr|; fora dele, até a ponta mais próxima. Isso dá pontas
  // arredondadas com brilho próprio, sem cunhas no brilho.
  float open = 1.0 + uScroll * 0.9;
  float lo = -44.5 * open, hi = 44.5 * open;
  float span = 360.0 - (hi - lo);
  float sweep = uDraw * span;
  float s = mod(deg - hi + 360.0, 360.0);
  float onArc = step(s, sweep);
  float endDeg = hi + sweep;
  vec2 eA = vec2(cos(radians(hi)), sin(radians(hi))) * rr;
  vec2 eB = vec2(cos(radians(endDeg)), sin(radians(endDeg))) * rr;
  float dEnds = min(length(p - eA), length(p - eB));
  d = mix(dEnds, d, onArc);
  float vis = step(0.0005, uDraw);
  float gapMask = vis;
  float drawn = 1.0;
  // Cabeça luminosa enquanto o anel está sendo traçado
  float head = exp(-pow(length(p - eB) * 14.0, 2.0)) * (1.0 - step(0.999, uDraw)) * vis;

  // Energia correndo ao longo do traço
  // Energia correndo ao longo do traço: a direção amostrada gira com o tempo
  float a1 = ang - t * 0.18;
  float a2 = ang + t * 0.13;
  float flow = fbm(vec2(cos(a1), sin(a1)) * 2.4 + vec2(r * 6.0 + t * 0.2, 0.0));
  float flow2 = fbm(vec2(cos(a2), sin(a2)) * 4.6 + 3.0);

  // Ponteiro: a luz se concentra no ponto do anel mais próximo do mouse
  vec2 m = (uMouse - uCenter) / R;
  float ma = atan(m.y, m.x);
  float da = abs(atan(sin(ang - ma), cos(ang - ma)));
  float near = exp(-da * da * 5.0) * uMouseOn * smoothstep(2.6, 0.6, length(m));

  float px = 1.0 / R;
  float w = 0.010 + px * 1.2;
  float core = smoothstep(w, 0.0, d);
  float glow = pow(0.018 / (d + 0.018), 1.6);
  float haze = exp(-d * 4.5) * 0.09;

  float body = (core * (0.75 + flow * 0.6) + glow * (0.42 + flow2 * 0.35 + near * 0.9)) * gapMask * drawn;
  body += head * (glow * 1.8 + core * 2.0) * gapMask;
  float amb = haze * gapMask * drawn * (0.6 + near);

  vec3 green = vec3(0.133, 0.773, 0.369);
  vec3 mint = vec3(0.60, 1.0, 0.76);
  vec3 col = green * (body * 0.9 + amb) + mint * core * gapMask * drawn * (0.35 + flow * 0.4);

  // Brilho interno, como luz presa dentro do anel
  float inner = smoothstep(1.0, 0.0, r) * 0.05 * uDraw;
  col += green * inner * (0.6 + wob);

  // Poeira de luz (bem sutil) em volta
  vec2 cell = floor(frag / 3.0);
  float star = step(0.9986, hash(cell)) * (0.5 + 0.5 * sin(t * 2.0 + hash(cell + 7.0) * 30.0));
  col += vec3(0.6, 0.9, 0.7) * star * 0.25 * smoothstep(2.4, 0.9, r);

  vec3 bg = vec3(0.0196, 0.0314, 0.0275);
  col = bg + col * uIntro * (1.0 + uScroll * 0.6);
  // Dither leve contra banding nos degradês
  col += (hash(frag + t) - 0.5) / 255.0;
  gl_FragColor = vec4(col, 1.0);
}
`;

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    // eslint-disable-next-line no-console
    console.warn('RingLight shader:', gl.getShaderInfoLog(sh));
    return null;
  }
  return sh;
}

function RingLight({ state, className = '' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas.parentElement;
    const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'high-performance' });
    if (!gl) {
      host.classList.add('no-webgl');
      return undefined;
    }

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) {
      host.classList.add('no-webgl');
      return undefined;
    }
    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const u = {};
    ['uRes', 'uTime', 'uCenter', 'uRadius', 'uMouse', 'uMouseOn', 'uIntro', 'uDraw', 'uScroll'].forEach((n) => {
      u[n] = gl.getUniformLocation(prog, n);
    });

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    let dpr = 1;
    let geo = { cx: 0, cy: 0, r: 1 };
    let cssH = 1;
    // Desenha um quadro agora. Só existe depois que ponteiro e relógio estão prontos.
    let render = null;

    const resize = () => {
      const rect = host.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, rect.width < 900 ? 1.25 : 1.5);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      cssH = rect.height;
      geo = ringGeometry(rect.width, rect.height);
      gl.viewport(0, 0, canvas.width, canvas.height);
      // Mudar o tamanho do canvas apaga o que estava desenhado. Redesenha na hora,
      // antes do navegador pintar, senão o anel some por um quadro (uma piscada).
      if (render) render(performance.now());
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(host);

    // Ponteiro suavizado (mola simples)
    const mouse = { x: 0, y: 0, tx: 0, ty: 0, on: 0, ton: 0 };
    const onMove = (e) => {
      const rect = host.getBoundingClientRect();
      mouse.tx = e.clientX - rect.left;
      mouse.ty = e.clientY - rect.top;
      mouse.ton = 1;
    };
    const onLeave = () => {
      mouse.ton = 0;
    };
    // O canvas fica atrás do texto: escutamos o ponteiro na seção inteira.
    const zone = host.closest('section') || host;
    // Mouse: segue o cursor. Toque: acende onde o dedo toca e arrasta.
    const onUp = (e) => e.pointerType !== 'mouse' && onLeave();
    if (!reduced) {
      zone.addEventListener('pointermove', onMove);
      zone.addEventListener('pointerdown', onMove);
      zone.addEventListener('pointerup', onUp);
      zone.addEventListener('pointercancel', onUp);
      if (finePointer) zone.addEventListener('pointerleave', onLeave);
    }

    let raf = 0;
    let visible = true;
    const start = performance.now();

    render = (now) => {
      const time = reduced ? 12.0 : (now - start) / 1000;
      gl.uniform2f(u.uRes, canvas.width, canvas.height);
      gl.uniform1f(u.uTime, time);
      gl.uniform2f(u.uCenter, geo.cx * dpr, (cssH - geo.cy) * dpr);
      gl.uniform1f(u.uRadius, geo.r * dpr);
      gl.uniform2f(u.uMouse, mouse.x * dpr, (cssH - mouse.y) * dpr);
      gl.uniform1f(u.uMouseOn, mouse.on);
      gl.uniform1f(u.uIntro, state.intro);
      gl.uniform1f(u.uDraw, state.draw);
      gl.uniform1f(u.uScroll, state.scroll);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const frame = (now) => {
      raf = 0;
      mouse.x += (mouse.tx - mouse.x) * 0.07;
      mouse.y += (mouse.ty - mouse.y) * 0.07;
      mouse.on += (mouse.ton - mouse.on) * 0.05;
      render(now);

      if (visible && !document.hidden && (!reduced || state.dirty)) {
        state.dirty = false;
        raf = requestAnimationFrame(frame);
      }
    };
    const kick = () => {
      if (!raf) raf = requestAnimationFrame(frame);
    };
    state.kick = () => {
      state.dirty = true;
      kick();
    };

    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) kick();
    });
    io.observe(host);
    const onVis = () => !document.hidden && kick();
    document.addEventListener('visibilitychange', onVis);
    kick();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      zone.removeEventListener('pointermove', onMove);
      zone.removeEventListener('pointerdown', onMove);
      zone.removeEventListener('pointerup', onUp);
      zone.removeEventListener('pointercancel', onUp);
      zone.removeEventListener('pointerleave', onLeave);
      state.kick = null;
      render = null;
      // Libera os recursos, mas não derruba o contexto: o canvas é o mesmo se o efeito
      // montar de novo (StrictMode em desenvolvimento), e um contexto perdido não volta.
      gl.deleteBuffer(buf);
      gl.deleteProgram(prog);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
    };
  }, [state]);

  return (
    <div className={`hv3-ringlight ${className}`} aria-hidden>
      <canvas ref={canvasRef} />
      <div className="hv3-ring-fallback" />
    </div>
  );
}

export default RingLight;
